import type { YaasPositionCardData, YaasPositionSplit } from "@solomon-apps/shared";

/**
 * Effective rewards routing for a position card — FE mirror of the backend
 * resolver (`yaas-api` `src/api/yaas/positions/effectiveRouting.ts`).
 *
 * A position's live payout config is EITHER saved-destination splits OR a single
 * destination address — never both (the backend nulls the single address when
 * splits are written, and clears splits when a single destination is set).
 * Precedence: splits → single address. Centralizing this rule keeps the
 * breakdown row, the "Current routing" section, and the advanced editor in
 * agreement about what the live routing actually is, instead of letting any
 * surface fall back to the saved-destinations address book as if it were state.
 */
export type EffectiveRouting =
    | { kind: "splits"; splits: YaasPositionSplit[] }
    | { kind: "single"; address: string; isSameWallet: boolean };

export function resolveEffectiveRouting(
    position: Pick<YaasPositionCardData, "rewards" | "splits">,
    connectedWalletAddress: string
): EffectiveRouting {
    if (position.splits.length > 0) {
        return { kind: "splits", splits: position.splits };
    }
    const address = position.rewards.destination_address;
    return { kind: "single", address, isSameWallet: address === connectedWalletAddress };
}

/**
 * Short label for the breakdown "Destination:" row. Preserves the existing
 * behavior: more than one split → "N destinations"; exactly one split → that
 * split's name; single self → "Same Wallet"; single other → truncated address.
 */
export function formatRoutingLabel(routing: EffectiveRouting): string {
    if (routing.kind === "splits") {
        return routing.splits.length > 1 ? `${routing.splits.length} destinations` : routing.splits[0].name;
    }
    return routing.isSameWallet ? "Same Wallet" : `${routing.address.slice(0, 4)}..${routing.address.slice(-4)}`;
}

/**
 * The single account a "Destination:" label can link to on Solscan, or
 * `null` when there isn't one.
 *
 * Every routing shape resolves to one address except splits across more
 * than one destination — "3 destinations" has no single account behind
 * it, so the caller renders plain text instead of a link rather than
 * picking one split arbitrarily.
 */
export function routingLinkAddress(routing: EffectiveRouting): string | null {
    if (routing.kind === "splits") {
        return routing.splits.length === 1 ? routing.splits[0].address : null;
    }
    return routing.address || null;
}
